import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import DataTable, { TableColumn } from "react-data-table-component";
import { Package, AlertTriangle, Euro, RefreshCcw, ShoppingCart, Building2, Download } from "lucide-react";
import { getApiBaseUrl } from "../utils/baseUrl";
import { useLoading, acquireTokenSafe } from "../App";
import DataSourcePanel from "./DataSourcePanel";

interface MasterService {
    Id: number;
    ServiceName: string;
    SKU: string;
    Description: string;
    IsRenewable: boolean;
    IsPurchasableOnline: boolean;
    IsBillable: boolean;
    MinAuthorisedPrice: number | null;
    MaxAuthorisedPrice: number | null;
    ServiceDescription: string;
    CreatedAt: string;
    UpdatedAt: string;
    EntryCount: number;
    TotalSeconds: number;
    LastLoggedAt: string | null;
}

interface DepartmentStats {
    code: string;
    name: string;
    count: number;
}

const departmentNames: { [key: string]: string } = {
    'AC': 'Accounts',
    'CL': 'Client Services',
    'CO': 'Company Secretarial',
    'GEN': 'General',
    'TECH': 'Technology',
    'SAGE': 'Sage',
    'XERO': 'Xero',
    'ACL': 'Accounts Client'
};

const MasterServicesList: React.FC = () => {
    const { instance, accounts } = useMsal();
    const { setLoading } = useLoading();
    const [services, setServices] = useState<MasterService[]>([]);
    const [filteredServices, setFilteredServices] = useState<MasterService[]>([]);
    const [search, setSearch] = useState("");
    const [departmentFilter, setDepartmentFilter] = useState<string>("all");
    const [usageFilter, setUsageFilter] = useState<"all" | "used" | "unused">("all");
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchServices = async () => {
            if (accounts.length === 0) {
                console.log('MasterServicesList: No accounts available yet');
                return;
            }

            setLoading(true);
            try {
                console.log('MasterServicesList: Acquiring token...');
                const token = await acquireTokenSafe(instance, accounts);
                if (!token) { setLoading(false); return; }
                console.log('MasterServicesList: Token acquired, fetching services...');

                const response = await fetch(`${getApiBaseUrl()}/api/master-services`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                console.log('MasterServicesList: Response status:', response.status);
                
                if (!response.ok) {
                    const errorText = await response.text();
                    console.error('MasterServicesList: Response error:', errorText);
                    throw new Error(`Failed to fetch services: ${response.status}`);
                }

                const data = await response.json();
                console.log('MasterServicesList: Received', data.length, 'services');
                setServices(data);
                setFilteredServices(data);
            } catch (err: any) {
                console.error('MasterServicesList: Error:', err);
                setError(err.message || 'Failed to load services');
            } finally {
                setLoading(false);
            }
        };

        fetchServices();
    }, [instance, accounts, setLoading]);

    // Get department from SKU
    const getDepartment = (sku: string): string => {
        const match = sku.match(/^([A-Z]+)_/);
        return match ? match[1] : 'OTHER';
    };

    // Get unique departments
    const departments: DepartmentStats[] = React.useMemo(() => {
        const deptCounts: { [key: string]: number } = {};
        services.forEach(s => {
            const dept = getDepartment(s.SKU);
            deptCounts[dept] = (deptCounts[dept] || 0) + 1;
        });
        return Object.entries(deptCounts)
            .map(([code, count]) => ({
                code,
                name: departmentNames[code] || code,
                count
            }))
            .sort((a, b) => b.count - a.count);
    }, [services]);

    useEffect(() => {
        let filtered = services;

        // Apply department filter
        if (departmentFilter !== "all") {
            filtered = filtered.filter(s => getDepartment(s.SKU) === departmentFilter);
        }

        // Apply usage filter (has time logged vs never used)
        if (usageFilter === "used") {
            filtered = filtered.filter(s => (s.EntryCount || 0) > 0);
        } else if (usageFilter === "unused") {
            filtered = filtered.filter(s => (s.EntryCount || 0) === 0);
        }

        // Apply search filter
        if (search) {
            const searchLower = search.toLowerCase();
            filtered = filtered.filter(
                (svc) =>
                    svc.ServiceName?.toLowerCase().includes(searchLower) ||
                    svc.SKU?.toLowerCase().includes(searchLower) ||
                    svc.Description?.toLowerCase().includes(searchLower) ||
                    svc.ServiceDescription?.toLowerCase().includes(searchLower)
            );
        }

        setFilteredServices(filtered);
    }, [search, departmentFilter, usageFilter, services]);

    const formatPrice = (price: number | null): string => {
        if (price === null || price === undefined) return '—';
        const num = typeof price === 'string' ? parseFloat(price) : price;
        if (isNaN(num)) return '—';
        if (num === 0) return '€0';
        return `€${num.toLocaleString('en-IE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
    };

    const columns: TableColumn<MasterService>[] = [
        {
            name: "Department",
            selector: (row) => getDepartment(row.SKU),
            sortable: true,
            cell: (row) => {
                const dept = getDepartment(row.SKU);
                return (
                    <span className={`dept-badge dept-${dept.toLowerCase()}`}>
                        {departmentNames[dept] || dept}
                    </span>
                );
            },
            width: "140px"
        },
        {
            name: "SKU",
            selector: (row) => row.SKU,
            sortable: true,
            cell: (row) => (
                <code className="sku-code">{row.SKU}</code>
            ),
            width: "160px"
        },
        {
            name: "Service Name",
            selector: (row) => row.ServiceName,
            sortable: true,
            cell: (row) => (
                <div className="service-cell">
                    <div className="service-name">{row.ServiceName}</div>
                    {row.Description && (
                        <div className="service-desc-short">{row.Description.substring(0, 60)}{row.Description.length > 60 ? '...' : ''}</div>
                    )}
                </div>
            ),
            grow: 2,
            minWidth: "250px"
        },
        {
            name: "Renewable",
            selector: (row) => row.IsRenewable ? 1 : 0,
            sortable: true,
            cell: (row) => (
                <span className={`status-badge ${row.IsRenewable ? 'renewable' : 'one-off'}`}>
                    {row.IsRenewable ? (
                        <><RefreshCcw size={12} /> Yes</>
                    ) : 'No'}
                </span>
            ),
            center: true,
            width: "100px"
        },
        {
            name: "Online",
            selector: (row) => row.IsPurchasableOnline ? 1 : 0,
            sortable: true,
            cell: (row) => (
                <span className={`status-badge ${row.IsPurchasableOnline ? 'online' : 'offline'}`}>
                    {row.IsPurchasableOnline ? (
                        <><ShoppingCart size={12} /> Yes</>
                    ) : 'No'}
                </span>
            ),
            center: true,
            width: "100px"
        },
        {
            name: "Billable",
            selector: (row) => row.IsBillable ? 1 : 0,
            sortable: true,
            cell: (row) => (
                <span className={`status-badge ${row.IsBillable ? 'renewable' : 'one-off'}`}>
                    {row.IsBillable ? 'Yes' : 'No'}
                </span>
            ),
            center: true,
            width: "100px"
        },
        {
            name: "Min Price",
            selector: (row) => row.MinAuthorisedPrice || 0,
            sortable: true,
            cell: (row) => (
                <span className="price-value">{formatPrice(row.MinAuthorisedPrice)}</span>
            ),
            right: true,
            width: "100px"
        },
        {
            name: "Max Price",
            selector: (row) => row.MaxAuthorisedPrice || 0,
            sortable: true,
            cell: (row) => (
                <span className="price-value">{formatPrice(row.MaxAuthorisedPrice)}</span>
            ),
            right: true,
            width: "100px"
        },
        {
            name: "Entries",
            selector: (row) => row.EntryCount || 0,
            sortable: true,
            cell: (row) => (
                <span className={`status-badge ${(row.EntryCount || 0) > 0 ? 'renewable' : 'one-off'}`}>
                    {(row.EntryCount || 0).toLocaleString('en-IE')}
                </span>
            ),
            center: true,
            width: "100px"
        },
        {
            name: "Hours",
            selector: (row) => row.TotalSeconds || 0,
            sortable: true,
            cell: (row) => {
                const hrs = (row.TotalSeconds || 0) / 3600;
                return (
                    <span className="price-value">
                        {hrs > 0 ? hrs.toLocaleString('en-IE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '—'}
                    </span>
                );
            },
            right: true,
            width: "100px"
        },
        {
            name: "Last Logged",
            selector: (row) => row.LastLoggedAt || '',
            sortable: true,
            cell: (row) => (
                <span className="cell-secondary">
                    {row.LastLoggedAt
                        ? new Date(row.LastLoggedAt).toLocaleDateString('en-IE', { year: 'numeric', month: 'short', day: 'numeric' })
                        : '—'}
                </span>
            ),
            width: "130px"
        }
    ];

    // Stats calculations
    const renewableCount = services.filter(s => s.IsRenewable).length;
    const onlineCount = services.filter(s => s.IsPurchasableOnline).length;
    const avgMinPrice = services.length > 0
        ? services.reduce((sum, s) => sum + (parseFloat(String(s.MinAuthorisedPrice)) || 0), 0) / services.length
        : 0;
    const avgMaxPrice = services.length > 0
        ? services.reduce((sum, s) => sum + (parseFloat(String(s.MaxAuthorisedPrice)) || 0), 0) / services.length
        : 0;

    // CSV Download function
    const downloadCSV = () => {
        const headers = [
            'Department',
            'SKU',
            'Service Name',
            'Description',
            'Is Renewable',
            'Is Purchasable Online',
            'Is Billable',
            'Min Authorised Price',
            'Max Authorised Price',
            'Service Description',
            'Entries',
            'Hours',
            'Last Logged'
        ];

        const csvData = filteredServices.map(service => [
            getDepartment(service.SKU),
            service.SKU,
            service.ServiceName,
            service.Description || '',
            service.IsRenewable ? 'Yes' : 'No',
            service.IsPurchasableOnline ? 'Yes' : 'No',
            service.IsBillable ? 'Yes' : 'No',
            service.MinAuthorisedPrice ?? '',
            service.MaxAuthorisedPrice ?? '',
            service.ServiceDescription || '',
            service.EntryCount || 0,
            ((service.TotalSeconds || 0) / 3600).toFixed(1),
            service.LastLoggedAt ? service.LastLoggedAt.split('T')[0] : ''
        ]);

        const escapeCSV = (value: any): string => {
            const str = String(value ?? '');
            if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
        };

        const csvContent = [
            headers.map(escapeCSV).join(','),
            ...csvData.map(row => row.map(escapeCSV).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `master-services-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (error) {
        return (
            <div className="page-container">
                <div className="page-header">
                    <div className="page-title">
                        <Package size={28} />
                        <h1>Master Services List</h1>
                    </div>
                </div>
                <div className="warning-banner">
                    <AlertTriangle size={20} />
                    <span>Error loading services: {error}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="page-header compact">
                <div className="page-title">
                    <Package size={20} />
                    <h1>Master Services List</h1>
                </div>
            </div>

            <div className="stats-grid stats-grid-6">
                <div className="stat-card">
                    <div className="stat-icon">
                        <Package size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{services.length}</div>
                        <div className="stat-label">Total Services</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">
                        <Building2 size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{departments.length}</div>
                        <div className="stat-label">Departments</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">
                        <RefreshCcw size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{renewableCount}</div>
                        <div className="stat-label">Renewable</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">
                        <ShoppingCart size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{onlineCount}</div>
                        <div className="stat-label">Online</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">
                        <Euro size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">€{avgMinPrice.toFixed(0)}</div>
                        <div className="stat-label">Avg Min Price</div>
                    </div>
                </div>
                <div className="stat-card highlight">
                    <div className="stat-icon">
                        <Euro size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">€{avgMaxPrice.toFixed(0)}</div>
                        <div className="stat-label">Avg Max Price</div>
                    </div>
                </div>
            </div>

            <div className="warning-banner compact">
                <AlertTriangle size={16} />
                <span>Pricing data — do not share without authorisation</span>
            </div>

            <div className="table-controls">
                <input
                    type="text"
                    placeholder="Search services..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="search-input"
                />
                <select
                    value={departmentFilter}
                    onChange={(e) => setDepartmentFilter(e.target.value)}
                    className="filter-select"
                >
                    <option value="all">All Departments ({services.length})</option>
                    {departments.map(dept => (
                        <option key={dept.code} value={dept.code}>
                            {dept.name} ({dept.count})
                        </option>
                    ))}
                </select>
                <select
                    value={usageFilter}
                    onChange={(e) => setUsageFilter(e.target.value as "all" | "used" | "unused")}
                    className="filter-select"
                    title="Filter by whether the SKU has any submitted/approved time entries"
                >
                    <option value="all">All SKUs ({services.length})</option>
                    <option value="used">
                        With time logged ({services.filter(s => (s.EntryCount || 0) > 0).length})
                    </option>
                    <option value="unused">
                        Never used ({services.filter(s => (s.EntryCount || 0) === 0).length})
                    </option>
                </select>
                <button
                    onClick={downloadCSV}
                    className="download-btn"
                    title="Download as CSV"
                >
                    <Download size={16} />
                    Export CSV
                </button>
            </div>

            <div className="data-table-container master-services-table">
                <DataTable
                    columns={columns}
                    data={filteredServices}
                    pagination
                    paginationPerPage={15}
                    paginationRowsPerPageOptions={[15, 25, 50, 100]}
                    highlightOnHover
                    responsive
                    noDataComponent={
                        <div className="no-data">No services found</div>
                    }
                    customStyles={{
                        table: {
                            style: {
                                backgroundColor: 'var(--bg-secondary)',
                                borderRadius: '8px',
                            },
                        },
                        headRow: {
                            style: {
                                backgroundColor: 'var(--bg-tertiary)',
                                borderBottomColor: 'var(--border-color)',
                                color: 'var(--text-primary)',
                                fontWeight: 600,
                                fontSize: '0.85rem',
                            },
                        },
                        headCells: {
                            style: {
                                color: 'var(--text-secondary)',
                                paddingLeft: '16px',
                                paddingRight: '16px',
                            },
                        },
                        rows: {
                            style: {
                                backgroundColor: 'var(--bg-secondary)',
                                color: 'var(--text-primary)',
                                borderBottomColor: 'var(--border-color)',
                            },
                            highlightOnHoverStyle: {
                                backgroundColor: 'var(--bg-hover)',
                                color: 'var(--text-primary)',
                                borderBottomColor: 'var(--border-color)',
                            },
                        },
                        cells: {
                            style: {
                                paddingLeft: '16px',
                                paddingRight: '16px',
                            },
                        },
                        pagination: {
                            style: {
                                backgroundColor: 'var(--bg-secondary)',
                                color: 'var(--text-secondary)',
                                borderTopColor: 'var(--border-color)',
                            },
                            pageButtonsStyle: {
                                color: 'var(--text-secondary)',
                                fill: 'var(--text-secondary)',
                                '&:hover:not(:disabled)': {
                                    backgroundColor: 'var(--bg-hover)',
                                },
                                '&:disabled': {
                                    opacity: 0.4,
                                },
                            },
                        },
                        noData: {
                            style: {
                                backgroundColor: 'var(--bg-secondary)',
                                color: 'var(--text-secondary)',
                            },
                        },
                    }}
                />
            </div>

            <DataSourcePanel
                summary={
                    <>
                        The master services list is the canonical catalogue of services
                        Kinore offers, including SKUs, descriptions, departmental
                        ownership and standard pricing. It is stored in the analytics
                        warehouse and edited via the Master Services Editor; the MKP app
                        reads from it to populate per-client elected services.
                    </>
                }
                sections={[
                    {
                        title: "APIs called",
                        items: [
                            {
                                name: "GET /api/master-services",
                                description: (
                                    <>
                                        Returns all rows from <code>master_services</code>
                                        ordered by department then service name. Read-only on
                                        this page; edits go through the Master Services Editor.
                                    </>
                                ),
                            },
                        ],
                    },
                    {
                        title: "Tables read (analytics Postgres)",
                        items: [
                            {
                                name: "master_services",
                                description:
                                    "SKU, service name, short/long description, department, base price, and active flag.",
                            },
                        ],
                    },
                ]}
            />

            <style>{`
                .master-services-table .sku-code {
                    font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
                    font-size: 0.8rem;
                    background: var(--bg-tertiary);
                    padding: 4px 8px;
                    border-radius: 4px;
                    color: var(--accent-color);
                }

                .master-services-table .service-cell {
                    padding: 8px 0;
                }

                .master-services-table .service-name {
                    font-weight: 500;
                    color: var(--text-primary);
                }

                .master-services-table .service-desc-short {
                    font-size: 0.75rem;
                    color: var(--text-muted);
                    margin-top: 2px;
                }

                .dept-badge.dept-ac { background: rgba(59, 130, 246, 0.15); color: #60a5fa; }
                .dept-badge.dept-cl { background: rgba(16, 185, 129, 0.15); color: #34d399; }
                .dept-badge.dept-co { background: rgba(168, 85, 247, 0.15); color: #c084fc; }
                .dept-badge.dept-gen { background: rgba(107, 114, 128, 0.15); color: #9ca3af; }
                .dept-badge.dept-tech { background: rgba(236, 72, 153, 0.15); color: #f472b6; }
                .dept-badge.dept-sage { background: rgba(34, 197, 94, 0.15); color: #4ade80; }
                .dept-badge.dept-xero { background: rgba(14, 165, 233, 0.15); color: #38bdf8; }
                .dept-badge.dept-acl { background: rgba(251, 146, 60, 0.15); color: #fb923c; }

                .master-services-table .price-value {
                    font-weight: 500;
                    color: var(--text-primary);
                }
            `}</style>
        </div>
    );
};

export default MasterServicesList;
